import * as React from 'react'
import { DadataFio, DaDataSuggestion } from '@http/dadata/models'
import { transformFioToDto } from '@http/service/shared-transformers'
import { Autocomplete } from '@shared/components/autocomplete'
import { InputRange } from '@shared/components/input-range'
import { TextField } from '@shared/components/text-field'
import { useAsyncAutocomplete } from '@shared/hooks/use-async-autocomplete'
import { ExternalDadataFioRequestRunner } from '@shared/hooks/use-async-autocomplete/request-runners'
import { usePhoneMask } from '@shared/hooks/use-phone-mask'
import { Paths } from '@shared/menu-items/paths'
import { getFullLinkByPath } from '@shared/pipes/dom'
import DownArrow from '@shared/svg/down-arrow.svg'
import { breaksHundreds, formatCalcSum } from '@shared/utils/helpers'
import { wordEnding } from '@shared/utils/wordEnding'
import { InvisibleSmartCaptcha, InvisibleSmartCaptchaProps } from '@yandex/smart-captcha'
import cx from 'classnames'
import { Form, FormikProvider, useFormik } from 'formik'
import { isEqual } from 'lodash'

import { ApplicationFormContext } from '../../Application'
import { CardNew } from '../../components/CardNew'
import { ConfirmSms } from '../../components/ConfirmSms'
import { disclaimerText, ProgressBarNew } from '../../components/ProgressBarNew'
import { Submit } from '../../components/Submit'
import { useCalculationResult } from '../../hooks/useCalculationResult'
import { useProgressBar } from '../../hooks/useProgressBar'
import { ApplicationFormProps } from '../../types'

import { sendSmsCode, updateExistedLead } from './api'
import { purposeOptions } from './data'
import { FormStatus } from './types'
import { validationSchema } from './validation'

import styles from './Primary.module.scss'

const maxStepSendSmsProgress = 60
const awardSendSmsForStep = 55
const maxStepConfirmSmsProgress = 75
export const awardConfirmSmsForStep = 15

const formatPeriodLabel = (value: number) =>
	`${value} ${wordEnding(value, { 1: 'год', 2: 'года', 5: 'лет' })}`
const formatLoanLabel = (value: number) => `${formatCalcSum(value)}`

export const Primary = (props: ApplicationFormProps) => {
	const context = React.useContext(ApplicationFormContext)
	const { formTitle, processBarText } = props
	const [token, setToken] = React.useState<string | null>(null)
	const [prevValues, setPrevValues] = React.useState(
		{} as (typeof context)['formController']['values']
	)
	const [isCaptchVisible, setIsCaptchaVisible] = React.useState(false)
	const formatResult = React.useCallback((value) => `от ${breaksHundreds(value.toFixed())} ₽`, [])

	const formik = useFormik<(typeof context)['formController']['values']>({
		initialValues: context.formController.values,
		validationSchema: validationSchema,
		isInitialValid: false,
		initialStatus: (context.hasLead ? 'update-existed-lead' : 'send-sms') as FormStatus,
		onSubmit: async (values, { setStatus, setSubmitting }) => {
			switch (formik.status as FormStatus) {
				case 'send-sms': {
					setSubmitting(true)

					try {
						await sendSmsCode(values.participant.phone, token!)
						context.formController.setValues(values)
						setStatus('confirm-sms' as FormStatus)
					} finally {
						setSubmitting(false)
					}

					return
				}

				case 'update-existed-lead': {
					if (isEqual(values, context.formController.values)) {
						context.setHasLead(false)
					} else {
						setSubmitting(true)

						try {
							await updateExistedLead(values)
							context.formController.refetch()
						} catch {
							setSubmitting(false)
						}
					}

					return
				}
			}
		},
	})

	const payment = useCalculationResult({
		loanPercent: props.loanPercent || 9.5,
		periodYears: formik.values.period,
		sum: formik.values.loan,
	})

	const progress = useProgressBar(formik.values.participant)

	const phoneInputRef = React.useRef<HTMLInputElement>(null!)

	const formStatus: FormStatus = formik.status
	const disableInputs = formik.isSubmitting

	const handlePhoneNumberForSms = React.useCallback(() => {
		// formik.setStatus('update-existed-lead' as FormStatus)
		formik.setStatus('send-sms')
		setPrevValues(formik.values)
		phoneInputRef.current?.focus()
	}, [formik.values])

	const submitProps: React.ComponentProps<typeof Submit> = {
		disabled: formik.isSubmitting,
		type: 'button',
		onClick: () =>
			formik.isValid
				? (() => {
						if (
							prevValues?.participant?.phone === formik.values.participant.phone &&
							prevValues?.purpose === formik.values.purpose &&
							prevValues?.participant?.fullname === formik.values.participant.fullname
						) {
							formik.setStatus('confirm-sms')
							return
						}
						setIsCaptchaVisible(true)
				  })()
				: (() => {
						formik.setTouched({
							purpose: true,
							participant: { email: true, phone: true, fullname: true },
						})
						formik.validateForm()
				  })(),
		text: 'Далее',
	}

	const { mask, maskChar, beforeMaskedValueChange } = usePhoneMask(false)

	const [fioAutocompleteOptions, , handleSetQuery] = useAsyncAutocomplete<
		DaDataSuggestion<DadataFio>
	>(new ExternalDadataFioRequestRunner())

	const fullnameProps: React.ComponentProps<typeof Autocomplete> = {
		label: 'Фамилия, имя и отчество',
		freeSolo: true,
		autoSelect: false,
		blurOnSelect: false,
		inputRequired: true,
		name: 'participant.fullname',
		getOptionSelected: React.useCallback((option, value) => value === option.value, []),
		onChange: React.useCallback(
			(value) =>
				formik.setValues({
					...formik.values,
					participant: { ...formik.values.participant, ...transformFioToDto(value, '') },
				}),
			[formik.values]
		),
		disabled: disableInputs,
		options: fioAutocompleteOptions,
		onInputChange: React.useCallback(
			(_, query) => {
				handleSetQuery(query)
				formik.setValues({
					...formik.values,
					participant: { ...formik.values.participant, ...transformFioToDto(query, '') },
				})
			},
			[formik.values]
		),
	}

	const phoneProps: React.ComponentProps<typeof TextField> = {
		name: 'participant.phone',
		value: formik.values.participant.phone,
		label: 'Номер телефона',
		disabled: disableInputs || formStatus === 'confirm-sms',
		inputMode: 'tel',
		inputRef: phoneInputRef,
		required: true,
		mask,
		maskChar,
		beforeMaskedValueChange,
	}

	const emailProps: React.ComponentProps<typeof TextField> = {
		name: 'participant.email',
		value: formik.values.participant.email,
		required: true,
		inputMode: 'email',
		disabled: disableInputs,
		label: 'Электронная почта',
	}

	const minLoan = props.minLoan || 2e5
	const maxLoan = props.maxLoan || 15e6
	const loanLabels = React.useMemo(() => {
		if (minLoan === 100000 && maxLoan === 500000) {
			return [100000, 200000, 300000, 400000, 500000].reduce((res, item) => {
				res[item] = formatLoanLabel(item)
				return res
			}, {})
		}

		return {
			[minLoan]: formatLoanLabel(minLoan),
			[maxLoan / 4]: formatLoanLabel(maxLoan / 4),
			[maxLoan / 2]: formatLoanLabel(maxLoan / 2),
			[(3 * maxLoan) / 4]: formatLoanLabel((3 * maxLoan) / 4),
			[maxLoan]: formatLoanLabel(maxLoan),
		}
	}, [minLoan, maxLoan])
	const loanProps: React.ComponentProps<typeof InputRange> = {
		view: 'new',
		value: formik.values.loan,
		onChange: React.useCallback((value) => formik.setFieldValue('loan', +value, false), []),
		min: minLoan,
		max: maxLoan,
		disabled: disableInputs,
		labels: loanLabels,
	}

	const minPeriod = props.minPeriod || 1
	const maxPeriod = props.maxPeriod || 20
	const periodLabels = React.useMemo(() => {
		if (minPeriod === 1 && maxPeriod === 5) {
			return [1, 2, 3, 4, 5].reduce((res, item) => {
				res[item] = formatPeriodLabel(item)
				return res
			}, {})
		}

		return {
			[minPeriod]: formatPeriodLabel(minPeriod),
			[maxPeriod / 4]: formatPeriodLabel(maxPeriod / 4),
			[maxPeriod / 2]: formatPeriodLabel(maxPeriod / 2),
			[(3 * maxPeriod) / 4]: formatPeriodLabel((3 * maxPeriod) / 4),
			[maxPeriod]: formatPeriodLabel(maxPeriod),
		}
	}, [minPeriod, maxPeriod])
	const periodProps: React.ComponentProps<typeof InputRange> = {
		view: 'new',
		value: formik.values.period,
		onChange: React.useCallback((value) => formik.setFieldValue('period', +value, false), []),
		min: minPeriod,
		max: maxPeriod,
		step: 1,
		disabled: disableInputs,
		labels: periodLabels,
	}

	const purposesProps: React.ComponentProps<typeof Autocomplete> = {
		label: 'Цель',
		blurOnSelect: false,
		value: formik.values.purpose,
		inputRequired: true,
		disabled: disableInputs,
		name: 'purpose',
		onChange: React.useCallback(
			(option) => formik.setFieldValue('purpose', option?.value || ''),
			[formik.values]
		),
		onInputChange: React.useCallback(
			(_, query) => formik.setFieldValue('purpose', query || ''),
			[]
		),
		options: purposeOptions,
		popupIcon: <DownArrow />,
	}

	const confirmSmsProps: React.ComponentProps<typeof ConfirmSms> = {
		phone: formik.values.participant.phone,
		changePhone: handlePhoneNumberForSms,
		leadProductType: props?.data?.leadProductType || 'REAL_ESTATE_LOAN',
	}

	const invisibleSmartCaptchaProps: InvisibleSmartCaptchaProps = {
		sitekey: 'ysc1_cjB1ueuzkk4kTWOfkxgPo2gLEOOmvrhWX4M1gwz667de5bd5',
		language: 'ru',
		hideShield: true,
		test: window.__env__.MODE === 'development',
		onSuccess: (token) => {
			setToken(token)
			setTimeout(formik.submitForm, 25)
		},
		visible: isCaptchVisible,
		onChallengeHidden: () => setIsCaptchaVisible(false),
	}

	return (
		<CardNew formTitle={formTitle}>
			<InvisibleSmartCaptcha {...invisibleSmartCaptchaProps} />
			<ProgressBarNew
				maxStepProgress={
					formStatus !== 'confirm-sms' ? maxStepSendSmsProgress : maxStepConfirmSmsProgress
				}
				text={processBarText}
				percent={progress}
				disclaimerText={
					formStatus !== 'confirm-sms'
						? disclaimerText(awardSendSmsForStep)
						: disclaimerText(awardConfirmSmsForStep, 'за подтверждение кода из СМС')
				}
				orderFromProps={formStatus === 'confirm-sms' ? 2 : undefined}
			/>

			<FormikProvider value={formik}>
				{formStatus !== 'confirm-sms' && (
					<Form className={styles.form} id='application-form-cross' autoComplete='off'>
						<div className={styles.rangeWrap}>
							<div className={styles.rangeInput}>
								<h3 className={styles.rangeLabel}>Сумма</h3>
								<InputRange {...loanProps} />
							</div>
							<div className={styles.rangeInput}>
								<h3 className={styles.rangeLabel}>Срок</h3>
								<InputRange {...periodProps} />
							</div>
							<div className={styles.rangeInputLast}>
								<h3 className={styles.rangeLabel}>Ежемесячный платеж</h3>
								<span className={styles.rangeVal}>{formatResult(payment)}</span>
							</div>
							<Submit classNameBtn={styles.submitDesktop} {...submitProps} />
						</div>

						<div className={styles.inputWrap}>
							<div className={styles.inputItem}>
								<Autocomplete {...fullnameProps} />
							</div>
							<div className={styles.inputItem}>
								<TextField {...phoneProps} />
							</div>
							<div className={styles.inputItem}>
								<TextField {...emailProps} />
							</div>
							<div className={cx(styles.inputItem)}>
								<Autocomplete {...purposesProps} />
							</div>
						</div>

						<div className={styles.privacy}>
							<Submit classNameBtn={styles.submitMobile} {...submitProps} />
							Нажимая кнопку «Далее», вы даете согласие
							<br />
							<a
								className='main-link'
								target='_blank'
								rel='noopener noreferrer nofollow'
								href={getFullLinkByPath(Paths.agreement)}
							>
								на обработку персональных данных
							</a>
						</div>
					</Form>
				)}
			</FormikProvider>
			{formStatus === 'confirm-sms' && <ConfirmSms {...confirmSmsProps} />}
		</CardNew>
	)
}
